import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { 
  Loader2, 
  ArrowLeft, 
  Building2, 
  CheckCircle, 
  Eye, 
  EyeOff,
  ShieldCheck,
  Clock
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { PasswordStrength } from '@/components/ui/password-strength';
import { z } from 'zod';

const registerSchema = z.object({
  agency_name: z.string().min(2, "Agency name must be at least 2 characters"),
  agency_code: z.string().min(2, "Code must be at least 2 characters").max(20).regex(/^[A-Za-z0-9_-]+$/, "Code must be alphanumeric").optional().or(z.literal('')),
  contact_email: z.string().email("Invalid email address"),
  admin_password: z.string().min(8, "Password must be at least 8 characters"),
  confirm_password: z.string(),
  admin_first_name: z.string().optional(),
  admin_last_name: z.string().optional(),
  phone: z.string().optional(),
}).refine((data) => data.admin_password === data.confirm_password, {
  message: "Passwords don't match",
  path: ["confirm_password"],
});

export default function AgencyRegisterPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredAgency, setRegisteredAgency] = useState<{ name: string; code: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [form, setForm] = useState({
    agency_name: '',
    agency_code: '',
    contact_email: '',
    admin_password: '',
    confirm_password: '',
    admin_first_name: '',
    admin_last_name: '',
    phone: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate form
    try {
      registerSchema.parse(form);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
        return;
      }
    }

    setLoading(true);

    try {
      const response = await supabase.functions.invoke('agency-register', {
        body: {
          agency_name: form.agency_name.trim(),
          agency_code: form.agency_code.trim() || undefined,
          contact_email: form.contact_email.trim(),
          admin_password: form.admin_password,
          admin_first_name: form.admin_first_name.trim() || undefined,
          admin_last_name: form.admin_last_name.trim() || undefined,
          phone: form.phone.trim() || undefined
        }
      });

      if (response.error) {
        throw new Error(response.error.message || 'Registration failed');
      }

      const data = response.data;

      if (data.error) {
        throw new Error(data.error);
      }

      setRegisteredAgency({
        name: data.agency.name,
        code: data.agency.code
      });
      setRegistrationComplete(true);

    } catch (error: any) {
      console.error('Registration error:', error);
      toast({
        title: "Registration Failed",
        description: error.message || "Failed to register agency",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (registrationComplete && registeredAgency) {
    return (
      <>
        <Helmet>
          <title>Registration Complete - CheckFirst</title>
        </Helmet>

        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="w-full max-w-md">
            <Card className="shadow-lg">
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <CardTitle className="text-2xl">Registration Successful!</CardTitle>
                <CardDescription>
                  Your agency has been registered and is pending approval
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-muted rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Agency Name:</span>
                    <span className="font-medium">{registeredAgency.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Agency Code:</span>
                    <span className="font-mono font-medium">{registeredAgency.code}</span>
                  </div>
                </div>

                <Alert>
                  <Clock className="h-4 w-4" />
                  <AlertTitle>Awaiting Approval</AlertTitle>
                  <AlertDescription>
                    Your registration is being reviewed by the CheckFirst team. 
                    You'll be able to login once your agency is approved. 
                    This typically takes 1-2 business days.
                  </AlertDescription>
                </Alert>

                <div className="space-y-3">
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full"
                  >
                    Go to Login
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/')}
                    className="w-full"
                  >
                    Return to Home
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Register Your Agency - CheckFirst</title>
        <meta name="description" content="Register your healthcare agency with CheckFirst. Get access to AI-powered patient triage software for your Dr Office or Home Care Agency." />
      </Helmet>

      <div className="min-h-screen flex items-center justify-center bg-background p-4 py-12">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Button>
            <div className="flex items-center justify-center gap-2 mb-2">
              <Building2 className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold text-foreground">CheckFirst</h1>
            </div>
            <p className="text-muted-foreground">
              Register your healthcare agency
            </p>
          </div>

          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" />
                Agency Registration
              </CardTitle>
              <CardDescription>
                Create an account for your Dr Office or Home Care Agency. 
                Registration is subject to approval.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Agency Information */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                    Agency Information
                  </h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="agency_name">Agency Name *</Label>
                    <Input
                      id="agency_name"
                      value={form.agency_name}
                      onChange={(e) => setForm(prev => ({ ...prev, agency_name: e.target.value }))}
                      placeholder="Metro Health Clinic"
                      required
                    />
                    {errors.agency_name && (
                      <p className="text-sm text-destructive">{errors.agency_name}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="agency_code">Agency Code (Optional)</Label>
                    <Input
                      id="agency_code"
                      value={form.agency_code}
                      onChange={(e) => setForm(prev => ({ ...prev, agency_code: e.target.value.toUpperCase() }))}
                      placeholder="MHC001"
                      maxLength={20}
                    />
                    <p className="text-xs text-muted-foreground">
                      A unique identifier for your agency. Auto-generated if left empty.
                    </p>
                    {errors.agency_code && (
                      <p className="text-sm text-destructive">{errors.agency_code}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                {/* Admin Account */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                    Admin Account
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="admin_first_name">First Name</Label>
                      <Input
                        id="admin_first_name"
                        value={form.admin_first_name}
                        onChange={(e) => setForm(prev => ({ ...prev, admin_first_name: e.target.value }))}
                        placeholder="John"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="admin_last_name">Last Name</Label>
                      <Input
                        id="admin_last_name"
                        value={form.admin_last_name}
                        onChange={(e) => setForm(prev => ({ ...prev, admin_last_name: e.target.value }))}
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact_email">Email Address *</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={form.contact_email}
                      onChange={(e) => setForm(prev => ({ ...prev, contact_email: e.target.value }))}
                      placeholder="admin@yourpractice.com"
                      required
                    />
                    {errors.contact_email && (
                      <p className="text-sm text-destructive">{errors.contact_email}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="admin_password">Password *</Label>
                    <div className="relative">
                      <Input
                        id="admin_password"
                        type={showPassword ? "text" : "password"}
                        value={form.admin_password}
                        onChange={(e) => setForm(prev => ({ ...prev, admin_password: e.target.value }))}
                        placeholder="Minimum 8 characters"
                        required
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                    <PasswordStrength password={form.admin_password} />
                    {errors.admin_password && (
                      <p className="text-sm text-destructive">{errors.admin_password}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirm Password *</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={form.confirm_password}
                      onChange={(e) => setForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                      placeholder="Confirm your password"
                      required
                    />
                    {errors.confirm_password && (
                      <p className="text-sm text-destructive">{errors.confirm_password}</p>
                    )}
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                  size="lg"
                >
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Register Agency
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  By registering, you agree to our{' '}
                  <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
                </p>
              </form>

              <div className="mt-6 pt-4 border-t text-center">
                <p className="text-sm text-muted-foreground">
                  Already have an account?{' '}
                  <Button
                    variant="link"
                    onClick={() => navigate('/login')}
                    className="p-0 h-auto"
                  >
                    Sign in here
                  </Button>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}